import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, RefreshCcw, CheckCircle2, XCircle, Banknote, Building, FileText, Search, Package, Tags, Scale } from 'lucide-react';
import SearchableSelect from '../SearchableSelect';
import { apiFetch } from '../../lib/api';
import { useTranslation } from 'react-i18next';

/* ── Helpers ── */
function formatRupiah(val) {
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return '';
  if (num >= 1e12) return `Rp ${(num / 1e12).toFixed(1)} Triliun`;
  if (num >= 1e9) return `Rp ${(num / 1e9).toFixed(0)} Miliar`;
  if (num >= 1e6) return `Rp ${(num / 1e6).toFixed(0)} Juta`;
  return `Rp ${num.toLocaleString('id-ID')}`;
}

/* ── Sub-components ── */
function FieldLabel({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6b7280', marginBottom: 8 }}>
      {children}
    </div>
  );
}

function InfoCard({ label, value, icon }) {
  return (
    <div style={{ display: 'flex', gap: 14 }}>
      <div style={{ width: 38, height: 38, borderRadius: '50%', flexShrink: 0, background: '#f0fdf4', border: '1.5px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981' }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2937', lineHeight: 1.55 }}>{value}</div>
      </div>
    </div>
  );
}

/* ── Main Component ── */
export default function TaxBeaMasuk() {
  const { t } = useTranslation();

  // State Form
  const [kategori, setKategori] = useState('');
  const [bidangUsaha, setBidangUsaha] = useState('');
  const [jenisBarang, setJenisBarang] = useState('');
  const [nilaiImpor, setNilaiImpor] = useState(''); // Opsional

  // State Options dari API
  const [availableKategori, setAvailableKategori] = useState([]);
  const [availableBidangUsaha, setAvailableBidangUsaha] = useState([]);
  const [availableJenisBarang, setAvailableJenisBarang] = useState([]);

  // State Status
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [loadError, setLoadError] = useState('');

  // Fetch Options
  useEffect(() => {
    let ignore = false;
    const params = new URLSearchParams();
    if (kategori) params.set('kategori', kategori);
    if (bidangUsaha) params.set('bidangUsaha', bidangUsaha);

    // Reset state di bawahnya jika parent berubah
    if (!kategori) { setAvailableBidangUsaha([]); setAvailableJenisBarang([]); }
    else if (!bidangUsaha) { setAvailableJenisBarang([]); }

    apiFetch(`/api/tax-bea-masuk/options?${params.toString()}`, { auth: false })
      .then(data => {
        if (ignore) return;
        setAvailableKategori(data.kategori || []);
        setAvailableBidangUsaha(data.bidangUsaha || []);
        setAvailableJenisBarang(data.jenisBarang || []);
        setLoadError('');
      })
      .catch(error => {
        if (ignore) return;
        setLoadError(error.message);
      });

    return () => { ignore = true; };
  }, [kategori, bidangUsaha]);

  // Handle Submit
  const handleSimulate = async () => {
    if (!kategori || !bidangUsaha || !jenisBarang) return;
    const importValue = nilaiImpor ? parseFloat(nilaiImpor) : 0;

    setIsCalculating(true);
    setResult(null);

    try {
      const data = await apiFetch('/api/tax-bea-masuk/simulate', {
        method: 'POST',
        auth: false,
        body: JSON.stringify({ kategori, bidangUsaha, jenisBarang, nilaiImpor: importValue })
      });
      setResult(data);
    } catch {
      setResult({ status: 'error', summary: '', detailInsentif: [], syarat: [] });
    }
    setIsCalculating(false);
  };

  const handleReset = () => {
    setKategori(''); setBidangUsaha(''); setJenisBarang(''); setNilaiImpor(''); setResult(null);
  };

  const handleDownload = () => {
    if (!result) return;
    const content = `LAPORAN ESTIMASI FASILITAS KEPABEANAN KNEKS\n\nStatus Kelayakan: ${result.status === 'success' ? 'Fasilitas Tersedia' : 'Kombinasi Tidak Ditemukan'}\nKategori: ${result.kategori}\nBidang Usaha: ${result.bidangUsaha}\nJenis Barang: ${result.jenisBarang}\nEstimasi Nilai Impor: Rp ${nilaiImpor ? parseFloat(nilaiImpor).toLocaleString('id-ID') : '0'}\n\nJENIS PEMBEBASAN: ${result.jenisPembebasan}\nCAKUPAN BARANG: ${result.summary}\n\nDETAIL INSENTIF:\n${result.detailInsentif.map((d, i) => `${i + 1}. ${d}`).join('\n')}\n\nPERSYARATAN ADMINISTRATIF:\n${result.syarat.map((s, i) => `${i + 1}. ${s}`).join('\n')}\n\nREFERENSI REGULASI: ${result.regulasi}\n\n---\n*Laporan ini merupakan hasil kalkulasi indikatif sistem simulasi.*`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `simulasi-bea-masuk-${Date.now()}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  const canSimulate = !isCalculating && kategori && bidangUsaha && jenisBarang;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'start' }}>

      {/* ── LEFT: Form Panel ── */}
      <div>
        <div style={{ marginBottom: 28 }}>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>Parameter Fasilitas Kepabeanan</h3>
          <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Masukkan informasi sektor dan jenis barang impor untuk mensimulasikan fasilitas pembebasan Bea Masuk.</p>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

          {/* Kategori Pemanfaatan */}
          <div>
            <FieldLabel><Tags size={10} style={{ marginRight: 4 }} />Kategori Pemanfaatan</FieldLabel>
            {loadError && <div style={{ fontSize: 12, color: '#ef4444', marginBottom: 8, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8 }}>{loadError}</div>}
            <SearchableSelect
              value={kategori}
              options={availableKategori}
              onChange={(v) => { setKategori(v); setBidangUsaha(''); setJenisBarang(''); setResult(null); }}
              placeholder="Pilih Kategori Utama"
              searchPlaceholder="Cari Kategori..."
            />
          </div>

          {/* Bidang Usaha (KBLI) */}
          <div>
            <FieldLabel><Building size={10} style={{ marginRight: 4 }} />Bidang Usaha (KBLI)</FieldLabel>
            <SearchableSelect
              value={bidangUsaha}
              options={availableBidangUsaha}
              onChange={(v) => { setBidangUsaha(v); setJenisBarang(''); setResult(null); }}
              placeholder="Pilih Bidang Usaha"
              disabled={!kategori}
              searchPlaceholder="Cari Bidang Usaha..."
            />
          </div>

          {/* Jenis Barang Impor */}
          <div>
            <FieldLabel><Package size={10} style={{ marginRight: 4 }} />Jenis Barang Impor</FieldLabel>
            <SearchableSelect
              value={jenisBarang}
              options={availableJenisBarang}
              onChange={(v) => { setJenisBarang(v); setResult(null); }}
              placeholder="Pilih Jenis Barang Impor"
              disabled={!bidangUsaha}
              searchPlaceholder="Cari Jenis Barang..."
            />
          </div>

          {/* Nilai Estimasi (Opsional) */}
          <div>
            <FieldLabel><Banknote size={10} style={{ marginRight: 4 }} />Estimasi Nilai Barang Impor (Opsional)</FieldLabel>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 14, fontWeight: 600, color: '#10b981', pointerEvents: 'none' }}>Rp</span>
              <input
                type="text"
                value={nilaiImpor ? parseInt(nilaiImpor).toLocaleString('id-ID') : ''}
                onChange={(e) => { const raw = e.target.value.replace(/\D/g, ''); setNilaiImpor(raw); }}
                placeholder="0"
                style={{ width: '100%', padding: '13px 100px 13px 46px', border: '2px solid #e5e7eb', borderRadius: 12, fontSize: 16, fontWeight: 600, color: '#111827', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: '#fff', transition: 'border-color 0.2s, box-shadow 0.2s' }}
                onFocus={e => { e.target.style.borderColor = '#10b981'; e.target.style.boxShadow = '0 0 0 4px rgba(16,185,129,0.08)'; }}
                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
              />
              {nilaiImpor && (
                <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 600, color: '#10b981', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '2px 8px' }}>
                  {formatRupiah(nilaiImpor)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
          <button onClick={handleSimulate} disabled={!canSimulate} style={{ flex: 1, padding: '14px 20px', borderRadius: 12, border: 'none', cursor: canSimulate ? 'pointer' : 'not-allowed', background: canSimulate ? 'linear-gradient(135deg,#10b981,#059669)' : '#e5e7eb', color: canSimulate ? '#fff' : '#9ca3af', fontSize: 14, fontWeight: 700, boxShadow: canSimulate ? '0 4px 14px rgba(16,185,129,0.3)' : 'none', transition: 'all 0.2s', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {isCalculating ? <motion.div animate={{ rotate: 360 }} transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }} style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%' }} /> : "Simulasikan Fasilitas"}
          </button>
          <button onClick={handleReset} title="Reset Parameter" style={{ padding: '14px 16px', borderRadius: 12, border: '2px solid #e5e7eb', background: '#fff', cursor: 'pointer', color: '#6b7280', transition: 'all 0.2s', fontFamily: 'inherit' }} onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.color = '#374151'; }} onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#6b7280'; }}>
            <RefreshCcw size={16} />
          </button>
        </div>
      </div>

      {/* ── RIGHT: Result Panel ── */}
      <div style={{ background: 'linear-gradient(145deg,#f8fafc,#f1f5f9)', borderRadius: 20, border: '1.5px solid #e2e8f0', minHeight: 460, padding: '32px 28px', display: 'flex', flexDirection: 'column' }}>
        <AnimatePresence mode="wait">

          {!result && !isCalculating && (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f0fdf4', border: '2px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <Search size={28} color="#10b981" />
              </div>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>{t("tax.siapMenganalisis")}</div>
              <p style={{ fontSize: 13, color: '#9ca3af', maxWidth: 260, lineHeight: 1.65, margin: 0 }}>Lengkapi kategori, bidang usaha, dan jenis barang impor di sebelah kiri untuk melihat fasilitas kepabeanan yang tersedia.</p>
            </motion.div>
          )}

          {isCalculating && (
            <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }} style={{ width: 52, height: 52, border: '3px solid #d1fae5', borderTopColor: '#10b981', borderRadius: '50%', marginBottom: 16 }} />
              <div style={{ fontSize: 13, fontWeight: 600, color: '#059669', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{t("tax.memprosesData")}</div>
            </motion.div>
          )}

          {result?.status === 'error' && (
            <motion.div key="error" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 14, padding: '16px 20px', display: 'flex', gap: 14 }}>
                <XCircle size={22} color="#ef4444" style={{ flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#dc2626', marginBottom: 4 }}>{t("tax.dataTidakDitemukan")}</div>
                  <div style={{ fontSize: 13, color: '#b91c1c', lineHeight: 1.55 }}>Kombinasi impor barang dan bidang usaha ini belum terdaftar dalam regulasi fasilitas pembebasan bea masuk.</div>
                </div>
              </div>
            </motion.div>
          )}

          {result?.status === 'success' && (
            <motion.div key="success" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#d1fae5', border: '1.5px solid #6ee7b7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckCircle2 size={18} color="#059669" />
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#059669' }}>Fasilitas Tersedia</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>Ditemukan fasilitas kepabeanan yang sesuai</div>
                  </div>
                </div>
                <button onClick={handleDownload} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 999, fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.borderColor = '#10b981'; }} onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e5e7eb'; }}>
                  <Download size={12} color="#10b981" />{t("tax.unduhLaporan")}
                </button>
              </div>

              <div style={{ background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)', border: '1.5px solid #bbf7d0', borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#059669', marginBottom: 6 }}>Kategori Pembebasan</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#052e16' }}>{result.jenisPembebasan}</div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20, paddingBottom: 20, borderBottom: '1.5px solid #e5e7eb' }}>
                <InfoCard label="Cakupan Barang" value={result.summary || 'Kesesuaian masterlist impor'} icon={<Package size={16} />} />
                <InfoCard label="Regulasi Acuan" value={result.regulasi || '-'} icon={<Scale size={16} />} />
              </div>

              <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
                {result.detailInsentif.length > 0 && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 10 }}>Rincian Fasilitas</div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {result.detailInsentif.map((d, i) => (
                        <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                          <CheckCircle2 size={14} color="#10b981" style={{ flexShrink: 0, marginTop: 2 }} />
                          <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.55 }}>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {result.syarat.length > 0 && (
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: 10 }}>{t("tax.persyaratanAdministratif")}</div>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {result.syarat.map((s, i) => (
                        <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                          <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#f3f4f6', border: '1.5px solid #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                            <span style={{ fontSize: 9, fontWeight: 700, color: '#6b7280' }}>{i + 1}</span>
                          </div>
                          <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.55 }}>{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}